import { Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { BehaviorSubject, Subject, take } from 'rxjs';
import { HttpService } from './http.service';

export interface AppNotification {
  id: string;
  title: string;
  message?: string;
  time?: string;
  link?: string;
  isRead?: boolean;
  createdby?: string;
  createdBy?: string;
  eventId?: string;
  createdAt?: string;
  eventName?: string;
  redirectUrl?: string;
  timeStamp?: string;
  timestamp?: string;
  userId?: string;
}

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private hubConnection!: signalR.HubConnection;
  private isConnected = false;
  private notificationsSubject = new BehaviorSubject<AppNotification[]>([]);
  private unreadCountSubject = new BehaviorSubject<number>(0);
  private newNotificationSubject = new Subject<AppNotification>();
  private currentUserId: string | null = null;

  notifications$ = this.notificationsSubject.asObservable();
  unreadCount$ = this.unreadCountSubject.asObservable();
  newNotification$ = this.newNotificationSubject.asObservable();

  constructor(private httpService: HttpService) {}

  setCurrentUserIdFromStorage() {
    try {
      const userDetailsStr = localStorage.getItem('userDetails');
      if (userDetailsStr) {
        const userDetails = JSON.parse(userDetailsStr);
        this.currentUserId =
          userDetails?.id || userDetails?.userId || userDetails?.email;
      }
      if (!this.currentUserId) {
        const userDetailsStr = localStorage.getItem('userDetails');
        if (userDetailsStr) {
          const userDetails = JSON.parse(userDetailsStr);
          this.currentUserId = userDetails?.email;
        }
      }
    } catch (e) {
      console.error('Error getting user ID:', e);
    }
  }

  setCurrentUserId(userId: string) {
    this.currentUserId = userId;
  }

  getCurrentUserId(): string | null {
    return this.currentUserId;
  }

  connect(token: string) {
    this.setCurrentUserIdFromStorage();

    if (this.hubConnection && this.isConnected) return;
    if (this.hubConnection) {
      this.hubConnection.stop();
    }

    const baseUrl = this.httpService.getApiUrl().replace('/api', '');

    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${baseUrl}/hubs/notifications`, {
        accessTokenFactory: () => token,
      })
      .withAutomaticReconnect()
      .build();

    this.registerListeners();

    this.hubConnection
      .start()
      .then(() => {
        console.log(
          '🔔 Notification socket connected for user:',
          this.currentUserId,
        );
        this.isConnected = true;
      })
      .catch((err) => console.error('socket error', err));
  }

  private registerListeners() {
    this.hubConnection.on('ReceiveNotification', (payload: any) => {
      const notificationUserId =
        payload.userId || payload.user_id || payload.userEmail || payload.email;
      if (this.currentUserId && notificationUserId) {
        const isSameUser = this.isSameUser(
          notificationUserId,
          this.currentUserId,
        );
        if (!isSameUser) {
          console.log('⚠️ Notification ignored - belongs to different user:', {
            notificationUser: notificationUserId,
            currentUser: this.currentUserId,
          });
          return;
        }
      }

      const notification: AppNotification = {
        id: payload.notificationId ?? payload.id ?? crypto.randomUUID(),
        title: payload.title || payload.message || 'New Notification',
        time: payload.timeStamp ?? payload.createdAt ?? 'Just now',
        timeStamp: payload.timeStamp ?? payload.createdAt,
        isRead: false,
        createdby: payload?.createdby || payload?.createdBy,
        message: payload.message,
        eventId: payload?.eventId,
        createdAt: payload.timeStamp || payload.createdAt,
        eventName: payload?.eventName,
        redirectUrl: payload?.redirectUrl,
        userId: notificationUserId,
      };

      const current = this.notificationsSubject.value;
      const isDuplicate = current.some((n) => n.id === notification.id);

      if (!isDuplicate) {
        const updated = [notification, ...current];
        this.notificationsSubject.next(updated);
        const unreadCount = updated.filter((n) => !n.isRead).length;
        this.unreadCountSubject.next(unreadCount);

        this.newNotificationSubject.next(notification);
        console.log(
          '✅ Notification added for current user:',
          notification.title,
        );
      }
    });
  }

  private isSameUser(user1: string, user2: string): boolean {
    if (!user1 || !user2) return false;
    return user1.toString().toLowerCase() === user2.toString().toLowerCase();
  }

  markAsRead(id: string) {
    const current = this.notificationsSubject.value;
    const updated = current.map((n) =>
      n.id === id ? { ...n, isRead: true } : n,
    );

    this.notificationsSubject.next(updated);
    const unreadCount = updated.filter((n) => !n.isRead).length;
    this.unreadCountSubject.next(unreadCount);
  }

  markAllAsRead() {
    const updated = this.notificationsSubject.value.map((n) => ({
      ...n,
      isRead: true,
    }));

    this.notificationsSubject.next(updated);
    this.unreadCountSubject.next(0);
  }

  refreshNotifications() {
    this.setCurrentUserIdFromStorage();

    return this.httpService
      .get<any[]>('notifications/notify-me')
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          const items = (Array.isArray(response) ? response : [])
            .filter((item) => this.isNotificationForCurrentUser(item))
            .map((item) => this.transformNotification(item));

          this.notificationsSubject.next(items);
          const unreadCount = items.filter((n) => !n.isRead).length;
          this.unreadCountSubject.next(unreadCount);

          console.log(
            `Notifications refreshed: ${items.length} items for user ${this.currentUserId}`,
          );
        },
        error: (err) => {
          console.error('Error refreshing notifications:', err);
        },
      });
  }

  getNotifications() {
    this.setCurrentUserIdFromStorage();

    return this.httpService
      .get<any[]>('notifications/notify-me')
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          const items = (Array.isArray(response) ? response : [])
            .filter((item) => this.isNotificationForCurrentUser(item))
            .map((item) => this.transformNotification(item));

          const current = this.notificationsSubject.value;
          const existingIds = new Set(current.map((n) => n.id));
          const newItems = items.filter((item) => !existingIds.has(item.id));

          if (newItems.length > 0) {
            const updated = [...newItems, ...current];
            this.notificationsSubject.next(updated);
            const unreadCount = updated.filter((n) => !n.isRead).length;
            this.unreadCountSubject.next(unreadCount);
          }
        },
        error: (err) => console.error('Error getting notifications:', err),
      });
  }

  private isNotificationForCurrentUser(item: any): boolean {
    if (!this.currentUserId) return true;
    const itemUserId =
      item.userId ||
      item.user_id ||
      item.userEmail ||
      item.email ||
      item.createdBy;

    if (!itemUserId) {
      return true;
    }

    return this.isSameUser(itemUserId, this.currentUserId);
  }

  private transformNotification(item: any): AppNotification {
    return {
      id: item.notificationId ?? item.id ?? crypto.randomUUID(),
      title: item.title ?? item.message ?? 'New notification',
      time: item.createdAt ?? item.timeStamp ?? 'Just now',
      timeStamp: item.timeStamp ?? item.createdAt,
      isRead: item?.isRead ?? false,
      createdby: item?.createdby || item?.createdBy,
      message: item?.message,
      eventId: item?.eventId,
      createdAt: item?.createdAt,
      eventName: item?.eventName,
      redirectUrl: item?.redirectUrl,
      userId: item.userId || item.user_id || item.userEmail,
    };
  }

  getWebNotify(token: string) {
    return this.httpService
      .get<any>('webnotify')
      .pipe(take(1))
      .subscribe({
        next: (response: any) => {
          console.log('🪧 Web Notification Connected');
          this.connect(token);
        },
        error: (err) => console.error('Web notify error:', err),
      });
  }

  disconnect() {
    if (this.hubConnection) {
      this.hubConnection.stop();
      this.hubConnection = undefined!;
      this.isConnected = false;
    }
  }

  clearAllNotifications() {
    this.notificationsSubject.next([]);
    this.unreadCountSubject.next(0);
    this.currentUserId = null;
  }
}
